import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Pagination
} from '@mui/material';
import { Add, Edit, Delete, MoreVert, Visibility, EmojiEvents as EmojiEventsIcon, Refresh as RefreshIcon, Gavel as GavelIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { storyService } from '../services/api';
import { 
  StoriesGridSkeleton, 
  LoadingSpinner
} from '../components/Loading/LoadingComponents';
import { useAsyncOperation } from '../hooks/useLoading';

const MyStories = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalStories, setTotalStories] = useState(0);
  const storiesPerPage = 12;

  // Enhanced loading
  const loading = useAsyncOperation('stories');

  useEffect(() => {
    const fetchMyStories = async () => {
      await loading.execute(
        async () => {
          console.log('MyStories: About to call getMyStories with pagination');
          const offset = (page - 1) * storiesPerPage;
          const response = await storyService.getMyStories({
            limit: storiesPerPage,
            offset: offset
          });
          console.log('MyStories: Full response:', response);
          console.log('MyStories: Response data:', response.data);
          console.log('MyStories: Stories array:', response.data?.data?.stories);
          console.log('MyStories: Stories length:', response.data?.data?.stories?.length);
          console.log('MyStories: Total stories:', response.data?.data?.total);
          
          setStories(response.data?.data?.stories || []);
          setTotalStories(response.data?.data?.total || 0);
          return response.data;
        },
        {
          phases: ['initial', 'processing'],
          onError: (err) => {
            setError('Failed to load your stories');
            console.error('Error fetching stories:', err);
            setShowErrorSnackbar(true);
          }
        }
      );
    };

    if (user) {
      fetchMyStories();
    }
  }, [user, page]);

  const handleMenuOpen = (event, story) => {
    setAnchorEl(event.currentTarget);
    setSelectedStory(story);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStory(null);
  };

  const handleEdit = () => {
    if (selectedStory) {
      navigate(`/edit/${selectedStory.story_id}`);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedStory) {
      navigate(`/story/${selectedStory.story_id}`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedStory && window.confirm('Are you sure you want to delete this story?')) {
      try {
        await storyService.deleteStory(selectedStory.story_id);
        setStories(stories.filter(story => story.story_id !== selectedStory.story_id));
        setTotalStories(prev => prev - 1);
      } catch (err) {
        setError('Failed to delete story');
      }
    }
    handleMenuClose();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const totalPages = Math.ceil(totalStories / storiesPerPage);

  const handleCreateNew = () => {
    navigate('/create');
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to view your stories.</Alert>
      </Container>
    );
  }

  if (loading.isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">
            My Stories
          </Typography>
        </Box>
        <StoriesGridSkeleton count={6} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          My Stories
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateNew}
        >
          Create New Story
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {stories.length === 0 ? (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No stories yet
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Start writing your first story!
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNew}
            sx={{ mt: 2 }}
          >
            Create Your First Story
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {stories.map((story) => (
            <Grid item xs={12} md={6} lg={4} key={story.story_id}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1, pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                        {story.title}
                      </Typography>
                      
                      {/* Event Icon after title */}
                      {story.event_id && story.event && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <EmojiEventsIcon fontSize="small" color="primary" />
                          <Typography variant="caption" color="primary">
                            {story.event.title}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Judge Feedback Indicator - Only show when feedback actually exists */}
                      {story.has_judging_feedback && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <GavelIcon fontSize="small" color="secondary" />
                          <Typography variant="caption" color="secondary">
                            Judge feedback available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, story)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {story.content}
                  </Typography>

                  {story.tags && story.tags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {story.tags.slice(0, 3).map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {story.tags.length > 3 && (
                        <Chip
                          label={`+${story.tags.length - 3} more`}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Bottom section with status and date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Chip
                      label={story.status === 'published' ? 'Published' : 'Draft'}
                      color={story.status === 'published' ? 'success' : 'default'}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(story.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/story/${story.story_id}`)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/edit/${story.story_id}`)}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <Visibility sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          Failed to load your stories. Please try again.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyStories;
